/**
 * API configuration.
 *
 * The API base URL and mock flag come from the generated PUBLIC config
 * (environments/<env>/public.yaml → src/config/public.ts). `EXPO_PUBLIC_API_URL`
 * remains an optional per-developer override for pointing a device at a LAN host;
 * it is a public value, never a secret.
 */

import Constants from "expo-constants";
import * as Device from "expo-device";
import { Platform } from "react-native";

import { publicConfig } from "@/config/public";

import { parseBoolEnv } from "./parse-bool-env";
import { selectApiBaseUrl } from "./select-api-base-url";

function cleanUrl(value: string | undefined): string | undefined {
  const trimmed = value?.replace(/\s+#.*$/, "").trim();
  return trimmed ? trimmed.replace(/\/+$/, "") : undefined;
}

// The overrides are ignored in a production build — see `select-api-base-url.ts`
// for the measured reason. In short: `EXPO_PUBLIC_*` is inlined into the bundle
// at build time, so a developer's `.env.local` was outranking production config
// in a real release artifact.
const fromEnv = cleanUrl(
  selectApiBaseUrl({
    environment: publicConfig.environment,
    envOverride: process.env.EXPO_PUBLIC_API_URL,
    extraOverride: Constants.expoConfig?.extra?.apiUrl as string | undefined,
    configuredUrl: publicConfig.apiBaseUrl,
  }),
);

/** Default to localhost during development; Android emulator needs 10.0.2.2. */
const devFallback = Platform.select({
  android: "http://10.0.2.2:3000",
  default: "http://localhost:3000",
});

function isLocalhost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function isPrivateIpv4(hostname: string): boolean {
  const parts = hostname.split(".").map((part) => Number(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isInteger(part))) {
    return false;
  }

  const [a, b] = parts;
  return (
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168)
  );
}

function hostFromValue(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;

  const raw = value.trim();
  const withScheme = /^[a-z][a-z\d+\-.]*:\/\//i.test(raw)
    ? raw
    : `http://${raw}`;

  try {
    const parsed = new URL(withScheme);
    return parsed.hostname && !isLocalhost(parsed.hostname)
      ? parsed.hostname
      : undefined;
  } catch {
    const host = raw.split(":")[0];
    return host && !isLocalhost(host) ? host : undefined;
  }
}

function getExpoHost(): string | undefined {
  return (
    hostFromValue(Constants.expoConfig?.hostUri) ??
    hostFromValue(Constants.expoGoConfig?.debuggerHost) ??
    hostFromValue(Constants.linkingUri)
  );
}

function normalizeLocalhostForNative(url: string): string {
  if (Platform.OS === "web") {
    // The mirror of the native rewrite below. One local .env serves phone AND
    // web dev, so EXPO_PUBLIC_API_URL usually holds the machine's LAN IP for
    // devices on the network — but a web page served from localhost calling a
    // LAN-IP API is CROSS-SITE, and the cookie-backed web session
    // (SameSite=Strict, auth-web.controller.ts) cannot work across sites: the
    // refresh cookie is never sent, and Chrome's third-party-cookie blocking
    // refuses to store it in the first place. When the page itself is on
    // localhost, the API is this same machine, so localhost is the honest
    // address — and localhost:8081 -> localhost:3000 is SAME-site (SameSite
    // ignores ports). A page opened via the LAN IP is left alone: there the
    // page and API hosts already match.
    try {
      const parsed = new URL(url);
      if (
        isPrivateIpv4(parsed.hostname) &&
        typeof window !== "undefined" &&
        isLocalhost(window.location.hostname)
      ) {
        parsed.hostname = window.location.hostname;
        return cleanUrl(parsed.toString()) ?? url;
      }
    } catch {
      // fall through to the untouched url
    }
    return url;
  }

  try {
    const parsed = new URL(url);

    if (
      Platform.OS === "android" &&
      !Device.isDevice &&
      (isLocalhost(parsed.hostname) || isPrivateIpv4(parsed.hostname))
    ) {
      parsed.hostname = "10.0.2.2";
      return cleanUrl(parsed.toString()) ?? url;
    }

    if (!isLocalhost(parsed.hostname)) return url;

    const expoHost = getExpoHost();
    if (expoHost) {
      parsed.hostname = expoHost;
    } else if (Platform.OS === "android") {
      parsed.hostname = "10.0.2.2";
    }

    return cleanUrl(parsed.toString()) ?? url;
  } catch {
    return url;
  }
}

export const API_BASE_URL = normalizeLocalhostForNative(
  fromEnv ?? devFallback ?? "http://localhost:3000",
);

/**
 * Whether to run against in-memory fixtures. Sourced from public config; an
 * EXPO_PUBLIC_USE_MOCK env var can still override locally.
 *
 * **Fail-closed, deliberately.** This used to read
 *
 *     (process.env.EXPO_PUBLIC_USE_MOCK ?? String(publicConfig.useMock)) !== "false"
 *
 * which turned mocks ON for anything that was not the exact lowercase string
 * `"false"` — `"0"`, `"FALSE"`, `"off"`, and, because `??` only guards
 * null/undefined, the EMPTY STRING. A variable defined-but-empty is an ordinary
 * CI/EAS accident, and the failure it produced is the worst kind: a release
 * build that works, looks populated, makes no network calls at all, and shows
 * fabricated people and Cues. Nothing about it looks broken.
 *
 * Now an unrecognised or empty override is ignored and config decides. Every
 * environment sets `useMock: false`, and the schema makes the field required,
 * so the only way to fixtures is to ask for them explicitly.
 */
export const USE_MOCK =
  parseBoolEnv(process.env.EXPO_PUBLIC_USE_MOCK) ?? publicConfig.useMock;

export function getApiConfigDebugInfo() {
  return {
    platform: Platform.OS,
    isDevice: Device.isDevice,
    publicEnv: publicConfig.environment,
    rawApiBaseUrl: fromEnv ?? null,
    apiBaseUrl: API_BASE_URL,
    useMock: USE_MOCK,
    expoHost: getExpoHost() ?? null,
    expoConfigHostUri: Constants.expoConfig?.hostUri ?? null,
    expoGoDebuggerHost: Constants.expoGoConfig?.debuggerHost ?? null,
    linkingUri: Constants.linkingUri ?? null,
  };
}

/** Socket.IO endpoint for the chat namespace. */
export const SOCKET_URL = `${API_BASE_URL}`;

/** Simulated latency for the mock layer (ms). */
export const MOCK_LATENCY = 450;
